// Pure helpers for reasoning about Cognito JWT lifetimes (no Amplify, no storage),
// shared by the session refresher, the cross-origin hand-off and the tenant-side seed.
//
// Cognito id/access tokens live ~1h; the refresh token much longer. Anything that is
// about to leave this page (a hand-off URL) or that a timer keeps alive should treat a
// token that expires within REFRESH_LEEWAY_MS as already stale, so the receiver never
// gets a token that dies in flight or a few minutes after landing.

/** Refresh when a token has less than this much life left. */
export const REFRESH_LEEWAY_MS = 5 * 60_000;

/** Decode a JWT payload without verifying it (claims only). Null if malformed. */
export const decodeJwtClaims = (token: string | null | undefined): Record<string, unknown> | null => {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
    const claims = JSON.parse(json);
    return claims && typeof claims === "object" ? (claims as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

/** Epoch ms at which the token expires, or null if it carries no readable `exp`. */
export const jwtExpiresAtMs = (token: string | null | undefined): number | null => {
  const exp = decodeJwtClaims(token)?.exp;
  return typeof exp === "number" && Number.isFinite(exp) ? exp * 1000 : null;
};

/** True if the token is missing/unreadable or expires within `withinMs` of `now`. */
export const isJwtExpiringWithin = (
  token: string | null | undefined,
  withinMs: number,
  now: number = Date.now(),
): boolean => {
  const at = jwtExpiresAtMs(token);
  return at === null || at - now <= withinMs;
};

/** True if the token is missing/unreadable or already expired. */
export const isJwtExpired = (token: string | null | undefined, now: number = Date.now()): boolean =>
  isJwtExpiringWithin(token, 0, now);

/**
 * How long to wait before proactively refreshing a session holding these tokens: until
 * `leewayMs` before the EARLIER of the id/access expiries (0 = refresh now). Null when
 * neither token has a readable expiry (nothing to schedule).
 */
export const msUntilRefresh = (
  tokens: { idToken?: string | null; accessToken?: string | null },
  now: number = Date.now(),
  leewayMs: number = REFRESH_LEEWAY_MS,
): number | null => {
  const expiries = [jwtExpiresAtMs(tokens.idToken), jwtExpiresAtMs(tokens.accessToken)].filter(
    (v): v is number => v !== null,
  );
  if (expiries.length === 0) return null;
  return Math.max(0, Math.min(...expiries) - leewayMs - now);
};
